import React, { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Input, Tag, Spin, Skeleton, Tooltip, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  ShoppingCartOutlined,
  DownOutlined,
  UpOutlined,
  FireOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useCart } from "../../services/CartContext";
import {
  // useBrandCampaigns,
  useCollections,
  useSKU,
} from "../../hooks/useProducts";
import { useDebounce } from "../../hooks/useDebounce";
import Flour from "../../assets/flour.png";
import AllIcon from "../../assets/allProducts.avif";
import "../../styles/products.css";

// --- Live Offer Countdown Component ---
const OfferCountdown = ({ offerEndDate }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!offerEndDate) return;

    const calculateTimeLeft = () => {
      const difference = +new Date(offerEndDate) - +new Date();
      if (difference <= 0) return null;

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [offerEndDate]);

  if (!timeLeft) return null;

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="product__offer-timer">
      <ClockCircleOutlined className="timer-icon" />
      <span className="timer-text">
        {timeLeft.days > 0 ? `${timeLeft.days}d ` : ""}
        {pad(timeLeft.hours)}h:{pad(timeLeft.minutes)}m:{pad(timeLeft.seconds)}s
      </span>
    </div>
  );
};

// --- Dynamic Quantity & Cart CTA ---
const QuantityControl = ({ product, cartItem, dispatch, isAvailable }) => {
  if (!isAvailable) {
    return (
      <button className="product__btn product__btn--disabled" disabled>
        Out of Stock
      </button>
    );
  }

  if (cartItem) {
    const maxStock =
      product.qty ?? product.stock ?? product.availableStock ?? Infinity;
    const isMaxReached = Number(cartItem.qty) >= Number(maxStock);

    const handleIncrement = (e) => {
      e.stopPropagation();

      // Show toast alert and block dispatch when limit is hit
      if (isMaxReached) {
        message.warning(`Maximum available stock (${maxStock}) reached`);
        return;
      }

      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {
          productId: product.productId,
          qty: cartItem.qty + 1,
        },
      });
    };

    const handleDecrement = (e) => {
      e.stopPropagation();
      if (cartItem.qty <= 1) {
        dispatch({
          type: "REMOVE_FROM_CART",
          payload: product.productId,
        });
      } else {
        dispatch({
          type: "UPDATE_QUANTITY",
          payload: {
            productId: product.productId,
            qty: cartItem.qty - 1,
          },
        });
      }
    };

    return (
      <div className="product__qty-stepper">
        <button
          type="button"
          className="qty-btn qty-btn--minus"
          onClick={handleDecrement}
          aria-label="Decrease quantity"
        >
          <MinusOutlined />
        </button>

        <span className="qty-value">{cartItem.qty}</span>

        <button
          type="button"
          className={`qty-btn qty-btn--plus ${isMaxReached ? "qty-btn--disabled" : ""}`}
          onClick={handleIncrement}
          disabled={isMaxReached}
          title={isMaxReached ? `Maximum stock (${maxStock}) reached` : ""}
          aria-label="Increase quantity"
        >
          <PlusOutlined />
        </button>
      </div>
    );
  }

  const handleAddToCart = (e) => {
    e.stopPropagation();
    const maxStock = product.qty ?? product.stock ?? 0;
    if (maxStock <= 0) {
      message.error("This item is out of stock");
      return;
    }

    dispatch({
      type: "ADD_TO_CART",
      payload: {
        productId: product.productId,
        title: product.title,
        displayPrice: product.price,
        image: product.image,
        piecePrice: product.piecePrice,
        qty: 1,
        conversionRate: product.conversionRate,
        displayUnitName: product.unitName,
      },
    });
  };

  return (
    <button className="product__btn" onClick={handleAddToCart}>
      <ShoppingCartOutlined style={{ marginRight: 6 }} /> Add To Cart
    </button>
  );
};

const Products = ({ campaignCode }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const debouncedSearch = useDebounce(searchTerm, 500);

  const selectedCategoryId = searchParams.get("categoryId");
  const selectedBrandId = searchParams.get("brandCampaignId");
  const brandCampaignId = selectedBrandId ? Number(selectedBrandId) : null;
  const collectionId = selectedCategoryId ? Number(selectedCategoryId) : null;

  const { cart, dispatch } = useCart();
  const [isExpanded, setIsExpanded] = useState(false);
  const productGridRef = useRef(null);

  const { data: collectionsData = [], isLoading: isCategoriesLoading } =
    useCollections();
  const collections = collectionsData.data || [];

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSKU(campaignCode, collectionId, brandCampaignId, 10, debouncedSearch);

  // const { data: brandsData = [], isLoading: isBrandsLoading } =
  //   useBrandCampaigns({
  //     isActive: true,
  //     top: 10,
  //   });
  // const brands = brandsData || [];

  const { ref, inView } = useInView();

  const scrollToProducts = () => {
    productGridRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleCategoryChange = (id) => {
    const params = new URLSearchParams(searchParams);
    if (!id) {
      params.delete("categoryId");
      params.delete("brandCampaignId");
      params.delete("search");
    } else {
      params.set("categoryId", id);
      params.delete("brandCampaignId");
      scrollToProducts();
    }
    setSearchParams(params);
  };

  // const handleBrandChange = (id) => {
  //   const params = new URLSearchParams(searchParams);
  //   if (!id) {
  //     params.delete("brandCampaignId");
  //   } else {
  //     params.set("brandCampaignId", id);
  //     params.delete("categoryId");
  //     scrollToProducts();
  //   }
  //   setSearchParams(params);
  // };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <section className="products" id="products">
      <div className="products__container">
        {/* Focus Brands Section */}
        {/* {brands.length > 0 && (
          <div className="products__content">
            <div className="categories__title">
              <h3>Focus Brands</h3>
            </div>
            <div className="focus__brands-grid">
              {isBrandsLoading ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                brands.map((brand) => {
                  const isActive = Number(selectedBrandId) === brand.id;
                  return (
                    <div
                      key={brand.id}
                      className={`focus__brand-card ${isActive ? "active" : ""}`}
                      onClick={() => handleBrandChange(brand.id)}
                    >
                      <img src={brand.imageUrl} alt={brand.name} />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )} */}

        {/* Categories Section */}
        <div className="products__content">
          <div className="categories__title">
            <h3>Top Categories</h3>
          </div>
          <div
            className={`product__categories-card ${
              isExpanded ? "expanded" : ""
            }`}
          >
            <div className="products__categories grid">
              <div
                className={`category__item ${
                  !selectedCategoryId ? "active" : ""
                }`}
                onClick={() => handleCategoryChange(null)}
              >
                <div className="category__image-div">
                  <img
                    src={AllIcon}
                    alt="All Products"
                    className="category-image"
                  />
                </div>
                <p className="category-title">All</p>
              </div>

              {isCategoriesLoading ? (
                <Skeleton active avatar paragraph={{ rows: 0 }} />
              ) : (
                collections.map((co) => {
                  const isActive =
                    Number(selectedCategoryId) === co.collectionId;
                  return (
                    <div
                      key={co.collectionId}
                      className={`category__item ${isActive ? "active" : ""}`}
                      onClick={() => handleCategoryChange(co.collectionId)}
                    >
                      <div className="category__image-div">
                        <img
                          src={co.imageUrl || Flour}
                          alt={co.name}
                          className="category-image"
                        />
                      </div>
                      <p className="category-title">{co.name}</p>
                    </div>
                  );
                })
              )}
            </div>

            <div
              className="product__categories-more"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <p className="more-categories">
                {isExpanded ? "View Less " : "View More "}
                {isExpanded ? <UpOutlined /> : <DownOutlined />}
              </p>
            </div>
          </div>
        </div>

        {/* Search Input Bar */}
        <div className="products__content">
          <div className="product__contents-tags">
            <div className="search__input">
              <Input
                size="large"
                placeholder="Search products by title, SKU, category..."
                prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                className="custom-ant-search"
              />
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="products__content">
          <div className="products__div grid" ref={productGridRef}>
            {isError ? (
              <div className="error-message">
                Failed to load products: {error?.message}
              </div>
            ) : isLoading ? (
              Array.from({ length: 8 }).map((_, idx) => (
                <div key={idx} className="products__card-skeleton">
                  <Skeleton.Image
                    active
                    style={{ width: "100%", height: 160 }}
                  />
                  <Skeleton active paragraph={{ rows: 2 }} />
                </div>
              ))
            ) : data?.pages?.flatMap((p) => p.items).length === 0 ? (
              <div className="no-products">
                <p>No products available matching your criteria.</p>
              </div>
            ) : (
              data?.pages?.map((page) =>
                page.items?.map((product) => {
                  const cartItem = cart.items.find(
                    (item) => item.productId === product.productId,
                  );
                  const isAvailable =
                    product.isSaleable === 1 && product.qty > 0;

                  // Offer checks
                  const isOfferActive =
                    product.isOnOffer && product.originalPrice > product.price;

                  const discountPct = isOfferActive
                    ? Math.round(
                        ((product.originalPrice - product.price) /
                          product.originalPrice) *
                          100,
                      )
                    : 0;

                  return (
                    <div className="products__card" key={product.productId}>
                      {/* Image + Badges Container */}
                      <div className="product__image-container">
                        {product.isAdult && (
                          <span className="badge adult-badge">18+</span>
                        )}

                        {/* Fire Offer Badge */}
                        {isOfferActive && (
                          <span className="badge offer-badge">
                            <FireOutlined /> {discountPct}% OFF
                          </span>
                        )}

                        <img
                          className="product__img"
                          src={product.image}
                          alt={product.title}
                          loading="lazy"
                        />

                        {/* Countdown Overlay Bar */}
                        {isOfferActive && product.offerEndsAt && (
                          <OfferCountdown offerEndDate={product.offerEndsAt} />
                        )}
                      </div>

                      {/* Info & Price Section */}
                      <div className="product__card-info">
                        <div className="product__meta">
                          <Tag
                            color={isOfferActive ? "volcano" : "blue"}
                            className="cat-tag"
                          >
                            {product.categories}
                          </Tag>
                          <span
                            className={`stock-indicator ${
                              isAvailable ? "in-stock" : "out-of-stock"
                            }`}
                          >
                            {isAvailable
                              ? `${product.qty} In Stock`
                              : "Out Of Stock"}
                          </span>
                        </div>

                        <Tooltip title={product.title}>
                          <h3 className="product__title">{product.title}</h3>
                        </Tooltip>

                        {/* Pricing Wrapper */}
                        <div className="product__price-section">
                          <div className="price-wrapper">
                            <span className="currency">Kes </span>
                            <span
                              className={`price-value ${
                                isOfferActive ? "price-value--offer" : ""
                              }`}
                            >
                              {product.price.toLocaleString()}
                            </span>
                            {isOfferActive && (
                              <span className="original-price">
                                Kes {product.originalPrice.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <span className="unit-label">
                            Per: {product.unitName}
                          </span>
                        </div>

                        {/* Quantity Stepper & Add to Cart */}
                        <QuantityControl
                          product={product}
                          cartItem={cartItem}
                          dispatch={dispatch}
                          isAvailable={isAvailable}
                        />
                      </div>
                    </div>
                  );
                }),
              )
            )}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={ref} className="infinite-scroll-loader">
            {isFetchingNextPage && <Spin size="large" />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
